/**
 * Brute-Force Rate Limiting Service
 * Enforces rate limits on failed authentication attempts:
 * - 5 consecutive failed attempts trigger a 15-minute temporary lockout
 */

const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 15 * 60 * 1000; // 15 minutes

const getStorageKey = (email) => `rate_limit_auth_${(email || "").trim().toLowerCase()}`;

/**
 * Check if an email is currently locked out
 * @param {string} email
 * @returns {{ isLocked: boolean, remainingSeconds: number, attempts: number }}
 */
export function checkLoginRateLimit(email) {
  if (!email) return { isLocked: false, remainingSeconds: 0, attempts: 0 };
  
  try {
    const raw = localStorage.getItem(getStorageKey(email));
    if (!raw) return { isLocked: false, remainingSeconds: 0, attempts: 0 };

    const data = JSON.parse(raw);
    const now = Date.now();

    if (data.lockoutUntil && data.lockoutUntil > now) {
      const remainingSeconds = Math.max(1, Math.ceil((data.lockoutUntil - now) / 1000));
      return {
        isLocked: true,
        remainingSeconds,
        attempts: data.attempts || MAX_FAILED_ATTEMPTS
      };
    }

    // If lockout expired, reset
    if (data.lockoutUntil && data.lockoutUntil <= now) {
      localStorage.removeItem(getStorageKey(email));
      return { isLocked: false, remainingSeconds: 0, attempts: 0 };
    }

    return {
      isLocked: false,
      remainingSeconds: 0,
      attempts: data.attempts || 0
    };
  } catch (err) {
    console.error("Rate limit check error:", err);
    return { isLocked: false, remainingSeconds: 0, attempts: 0 };
  }
}

/**
 * Record a failed login attempt
 * @param {string} email
 * @returns {{ isLocked: boolean, remainingSeconds: number, attempts: number }}
 */
export function recordFailedAttempt(email) {
  if (!email) return { isLocked: false, remainingSeconds: 0, attempts: 0 };

  try {
    const key = getStorageKey(email);
    const raw = localStorage.getItem(key);
    let data = raw ? JSON.parse(raw) : { attempts: 0, lockoutUntil: null };

    data.attempts = (data.attempts || 0) + 1;

    if (data.attempts >= MAX_FAILED_ATTEMPTS) {
      data.lockoutUntil = Date.now() + LOCKOUT_DURATION_MS;
      localStorage.setItem(key, JSON.stringify(data));
      return {
        isLocked: true,
        remainingSeconds: Math.ceil(LOCKOUT_DURATION_MS / 1000),
        attempts: data.attempts
      };
    }

    localStorage.setItem(key, JSON.stringify(data));
    return {
      isLocked: false,
      remainingSeconds: 0,
      attempts: data.attempts
    };
  } catch (err) {
    console.error("Record failed attempt error:", err);
    return { isLocked: false, remainingSeconds: 0, attempts: 0 };
  }
}

/**
 * Reset failed attempts after successful authentication
 * @param {string} email
 */
export function resetFailedAttempts(email) {
  if (!email) return;
  try {
    localStorage.removeItem(getStorageKey(email));
  } catch (err) {
    console.error("Reset failed attempts error:", err);
  }
}
